import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import type {
  ConferenceProceeding,
  ConferenceProceedingMetadata,
} from '@generated/form-types/conference-proceeding';
import {
  createCrossrefDocument,
  appendElement,
  appendElementIf,
  serializeXml,
} from '../xml-helpers';
import {
  buildHead,
  buildPublicationDates,
  buildIsbns,
  buildDoiData,
  buildTitles,
  buildPublisher,
  buildSeriesMetadata,
} from '../builders';
import { buildConferencePaper } from './builders';

/**
 * Builds the shared proceedings fields into a container element
 * (`<proceedings_metadata>` or `<proceedings_series_metadata>`).
 */
function buildProceedingFields(
  container: Element,
  meta: ConferenceProceedingMetadata
): void {
  // 1. proceedings_title
  appendElement(container, 'proceedings_title', meta.proceedingsTitle);

  // 2. publisher
  buildPublisher(container, meta.publisher);

  // 3. publication_date
  buildPublicationDates(container, meta.publicationDates);

  // 4. isbn / noisbn
  buildIsbns(container, meta.isbn, 'simple_series');

  // 5. doi_data
  buildDoiData(container, meta.doi, meta.landingPageUrl);
}

export class ConferenceProceedingXmlRenderingStrategy
  implements XMLGenerationStrategy
{
  async generateXML(
    _recordType: ContentRegistrationType,
    data: FormData,
    prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const proceedingData = data as unknown as ConferenceProceeding;
    const proceedingMetadata = proceedingData.proceedingMetadata;
    const hadData = origin?.resourceFieldsImported;

    const doc = createCrossrefDocument();
    const root = doc.documentElement;

    // <head>
    buildHead(root, userInfo);

    // <body>
    const body = appendElement(root, 'body');
    const conference = appendElement(body, 'conference');

    // <event_metadata>
    const eventMeta = appendElement(conference, 'event_metadata');
    appendElement(
      eventMeta,
      'conference_name',
      proceedingMetadata.conferenceName
    );
    appendElementIf(
      eventMeta,
      'conference_acronym',
      proceedingMetadata.conferenceAcronym
    );

    if (proceedingData.series) {
      // <proceedings_series_metadata>
      const procSeriesMeta = appendElement(
        conference,
        'proceedings_series_metadata'
      );

      // series_metadata
      buildSeriesMetadata(procSeriesMeta, proceedingData.series);

      buildProceedingFields(procSeriesMeta, proceedingMetadata);
    } else {
      // <proceedings_metadata>
      const procMeta = appendElement(conference, 'proceedings_metadata');
      buildProceedingFields(procMeta, proceedingMetadata);
    }

    // <conference_paper> elements
    if (proceedingData.papers) {
      for (const paper of proceedingData.papers) {
        buildConferencePaper(conference, paper, {
          simCheckUrl:
            hadData?.simCheckUrl === true && !paper.similarityCheckUrl?.trim(),
          references:
            hadData?.references === true && !paper.references?.trim(),
        });
      }
    }

    return serializeXml(doc, prettyPrint);
  }
}
